import React, { useState } from 'react';
import { 
  ArrowLeft, Plus, Search, Filter, CheckCircle, XCircle, AlertCircle, Clock,
  PackageCheck, Microscope, ClipboardCheck, Truck, CalendarCheck
} from 'lucide-react';
import { inspectionRecords, inspectionTypes, inspectionResults } from '../../data/qualityControlData';
import { products } from '../../data/inventoryData';

const InspectionsList = ({ onBack, onViewDetail, onCreateNew }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterResult, setFilterResult] = useState('all');
  
  // Get product details
  const getProductDetails = (productId) => {
    return products.find(p => p.id === productId) || { name: '未知產品', sku: '' };
  };
  
  // Get inspection type details
  const getInspectionTypeDetails = (typeId) => {
    return inspectionTypes.find(t => t.id === typeId) || { name: '其他檢驗', color: '#6B7280' };
  };
  
  // Get result details
  const getResultDetails = (resultId) => {
    return inspectionResults.find(r => r.id === resultId) || { name: '未知', color: '#6B7280' };
  };
  
  // Get result icon
  const getResultIcon = (result) => {
    switch(result) {
      case 'passed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'conditional':
        return <AlertCircle className="h-5 w-5 text-amber-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };
  
  // Get inspection type icon
  const getInspectionTypeIcon = (type) => {
    switch(type) {
      case 'incoming':
        return <PackageCheck className="h-5 w-5 text-blue-500" />;
      case 'in-process':
        return <Microscope className="h-5 w-5 text-purple-500" />;
      case 'final':
        return <ClipboardCheck className="h-5 w-5 text-green-500" />;
      case 'outgoing':
        return <Truck className="h-5 w-5 text-amber-500" />;
      case 'periodic':
        return <CalendarCheck className="h-5 w-5 text-pink-500" />;
      default:
        return <ClipboardCheck className="h-5 w-5 text-gray-500" />;
    }
  };
  
  // Filter inspections
  const filteredInspections = inspectionRecords.filter(inspection => {
    const product = getProductDetails(inspection.product_id);
    const matchesSearch = 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inspection.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || inspection.inspection_type === filterType;
    const matchesResult = filterResult === 'all' || inspection.result === filterResult;
    
    return matchesSearch && matchesType && matchesResult;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button 
            onClick={onBack}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <h1 className="text-2xl font-bold">檢驗記錄</h1>
        </div>
        <button
          onClick={onCreateNew}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          新增檢驗
        </button>
      </div>
      
      {/* Search and Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            placeholder="搜尋產品或檢驗編號..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex items-center space-x-2 min-w-[200px]">
          <Filter className="h-4 w-4 text-gray-500" />
          <select
            className="block w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">所有檢驗類型</option>
            {inspectionTypes.map(type => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>
        </div>
        
        <div className="flex items-center space-x-2 min-w-[200px]">
          <Filter className="h-4 w-4 text-gray-500" />
          <select
            className="block w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            value={filterResult}
            onChange={(e) => setFilterResult(e.target.value)}
          >
            <option value="all">所有檢驗結果</option>
            {inspectionResults.map(result => (
              <option key={result.id} value={result.id}>{result.name}</option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Inspections List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-6 py-3 text-left">檢驗編號</th>
                <th className="px-6 py-3 text-left">檢驗類型</th>
                <th className="px-6 py-3 text-left">產品</th>
                <th className="px-6 py-3 text-center">檢驗數量</th>
                <th className="px-6 py-3 text-center">合格數量</th>
                <th className="px-6 py-3 text-left">檢驗日期</th>
                <th className="px-6 py-3 text-center">檢驗結果</th>
                <th className="px-6 py-3 text-center">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredInspections.map(inspection => {
                const product = getProductDetails(inspection.product_id);
                const typeDetails = getInspectionTypeDetails(inspection.inspection_type);
                const resultDetails = getResultDetails(inspection.result);
                
                return (
                  <tr key={inspection.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {inspection.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <div className="p-1 rounded-md mr-2" style={{ backgroundColor: `${typeDetails.color}20` }}>
                          {getInspectionTypeIcon(inspection.inspection_type)}
                        </div>
                        {typeDetails.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        <div className="font-medium text-gray-900">{product.name}</div>
                        <div className="text-xs text-gray-500">{product.sku}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                      {inspection.quantity_inspected}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                      {inspection.quantity_passed}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {inspection.inspection_date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center">
                        <span 
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" 
                          style={{ 
                            backgroundColor: `${resultDetails.color}20`,
                            color: resultDetails.color 
                          }}
                        >
                          {getResultIcon(inspection.result)}
                          <span className="ml-1">{resultDetails.name}</span>
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                      <button 
                        onClick={() => onViewDetail(inspection.id)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        查看詳情
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {filteredInspections.length === 0 && (
          <div className="py-8 text-center text-gray-500">
            沒有符合條件的檢驗記錄
          </div>
        )}
      </div>
    </div>
  );
};

export default InspectionsList;
